import type { DailyPnlPoint } from '@et/shared';
import { useMemo, useRef, useState, type PointerEvent } from 'react';
import { dateTime, money, signed } from '../lib/format';
import { useT } from '../lib/i18n';

/**
 * Hand-rolled SVG charts (dataviz reference method): one axis, recessive grid, 2 px line,
 * hover crosshair + tooltip, a table view for every chart. Values are converted to numbers
 * ONLY to place pixels — every figure a person reads comes from the original decimal
 * string (AI_RULES §5: display happens at the UI edge).
 */

const W = 720;
const H = 240;
const PAD = { top: 12, right: 12, bottom: 24, left: 64 };
const px = (v: string): number => Number.parseFloat(v);

function ticks(min: number, max: number, count = 4): number[] {
  if (min === max) return [min];
  const step = (max - min) / count;
  return Array.from({ length: count + 1 }, (_, i) => min + step * i);
}

export function EquityChart({ points }: { points: { ts: string; equity: string }[] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const box = useRef<SVGSVGElement>(null);
  const t = useT();

  const geo = useMemo(() => {
    if (points.length === 0) return null;
    const xs = points.map((p) => Date.parse(p.ts));
    const ys = points.map((p) => px(p.equity));
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    let y0 = Math.min(...ys);
    let y1 = Math.max(...ys);
    const padY = (y1 - y0) * 0.1 || y1 * 0.001 || 1;
    y0 -= padY;
    y1 += padY;
    const sx = (x: number): number => PAD.left + (x1 === x0 ? 0.5 : (x - x0) / (x1 - x0)) * (W - PAD.left - PAD.right);
    const sy = (y: number): number => PAD.top + (1 - (y - y0) / (y1 - y0)) * (H - PAD.top - PAD.bottom);
    const path = points.map((_p, i) => `${i === 0 ? 'M' : 'L'}${sx(xs[i] ?? 0).toFixed(1)},${sy(ys[i] ?? 0).toFixed(1)}`).join(' ');
    return { xs, ys, sx, sy, path, yTicks: ticks(y0, y1), x0, x1 };
  }, [points]);

  if (!geo) return <div className="empty">{t('No equity snapshots yet.')}</div>;

  const onMove = (e: PointerEvent<SVGSVGElement>): void => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0;
    for (let i = 1; i < geo.xs.length; i++) {
      if (Math.abs(geo.sx(geo.xs[i] ?? 0) - x) < Math.abs(geo.sx(geo.xs[best] ?? 0) - x)) best = i;
    }
    setHover(best);
  };

  const hp = hover === null ? null : points[hover];
  const hx = hover === null ? 0 : geo.sx(geo.xs[hover] ?? 0);
  const hy = hover === null ? 0 : geo.sy(geo.ys[hover] ?? 0);

  return (
    <div>
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: -32, marginBottom: 8 }}>
        <button className="small" onClick={() => { setAsTable(!asTable); }}>{asTable ? t('Chart') : t('Table')}</button>
      </div>
      {asTable ? (
        <div className="table-wrap" style={{ maxHeight: 260 }}>
          <table><thead><tr><th>{t('Time')}</th><th className="num">{t('Equity (USDT)')}</th></tr></thead>
            <tbody>{points.map((p) => <tr key={p.ts}><td>{dateTime(p.ts)}</td><td className="num">{money(p.equity)}</td></tr>)}</tbody>
          </table>
        </div>
      ) : (
        <div className="chart-box">
          <svg ref={box} className="chart" viewBox={`0 0 ${String(W)} ${String(H)}`} role="img" aria-label={t('Equity over time in USDT')} onPointerMove={onMove} onPointerLeave={() => { setHover(null); }}>
            {geo.yTicks.map((t) => (
              <g key={t}>
                <line className="gridline" x1={PAD.left} x2={W - PAD.right} y1={geo.sy(t)} y2={geo.sy(t)} />
                <text className="axis-label" x={PAD.left - 8} y={geo.sy(t) + 4} textAnchor="end">{money(t.toFixed(2), 0)}</text>
              </g>
            ))}
            <text className="axis-label" x={PAD.left} y={H - 6}>{dateTime(new Date(geo.x0).toISOString())}</text>
            <text className="axis-label" x={W - PAD.right} y={H - 6} textAnchor="end">{dateTime(new Date(geo.x1).toISOString())}</text>
            <path className="line" d={geo.path} />
            {hp && (
              <g>
                <line className="crosshair" x1={hx} x2={hx} y1={PAD.top} y2={H - PAD.bottom} />
                <circle className="marker" cx={hx} cy={hy} r={5} />
              </g>
            )}
            <rect x={PAD.left} y={0} width={W - PAD.left - PAD.right} height={H} fill="transparent" />
          </svg>
          {hp && (
            <div className="chart-tip" style={{ left: `${String((hx / W) * 100)}%`, top: `${String((hy / H) * 100)}%` }}>
              <div className="muted">{dateTime(hp.ts)}</div>
              <div className="num">{money(hp.equity)} USDT</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PnlBars({ days }: { days: DailyPnlPoint[] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const t = useT();
  if (days.length === 0) return <div className="empty">{t('No closed trades in this range.')}</div>;

  const vals = days.map((d) => px(d.realizedPnl));
  const max = Math.max(...vals.map(Math.abs), 1);
  const h = 160;
  const zeroY = h / 2;
  const slot = (W - PAD.left - PAD.right) / days.length;
  const barW = Math.max(2, Math.min(28, slot - 2)); // 2 px surface gap between bars

  return (
    <div className="chart-box">
      <svg className="chart" viewBox={`0 0 ${String(W)} ${String(h + 20)}`} role="img" aria-label={t('Realized PnL per day, gains blue above zero, losses red below')} onPointerLeave={() => { setHover(null); }}>
        <line className="zero" x1={PAD.left} x2={W - PAD.right} y1={zeroY} y2={zeroY} />
        <text className="axis-label" x={PAD.left - 8} y={zeroY + 4} textAnchor="end">0</text>
        {days.map((d, i) => {
          const v = vals[i] ?? 0;
          const len = (Math.abs(v) / max) * (zeroY - 6);
          const x = PAD.left + i * slot + (slot - barW) / 2;
          const y = v >= 0 ? zeroY - len : zeroY;
          return (
            <g key={d.date} onPointerEnter={() => { setHover(i); }}>
              <rect x={PAD.left + i * slot} y={0} width={slot} height={h} fill="transparent" />
              <rect className={v >= 0 ? 'bar-pos' : 'bar-neg'} x={x} y={y} width={barW} height={Math.max(len, 1)} rx={Math.min(4, barW / 2)} />
            </g>
          );
        })}
        <text className="axis-label" x={PAD.left} y={h + 16}>{days[0]?.date}</text>
        <text className="axis-label" x={W - PAD.right} y={h + 16} textAnchor="end">{days[days.length - 1]?.date}</text>
      </svg>
      {hover !== null && days[hover] && (
        <div className="chart-tip" style={{ left: `${String(((PAD.left + (hover + 0.5) * slot) / W) * 100)}%`, top: '20%' }}>
          <div className="muted">{days[hover].date}</div>
          <div className="num">{signed(days[hover].realizedPnl)} USDT</div>
          <div className="muted">{t('{trades} trades · {wins} wins', { trades: days[hover].trades, wins: days[hover].wins })}</div>
        </div>
      )}
    </div>
  );
}
